import type { RenderNode } from "./types.js";

type InteractionNode = { node: RenderNode; parentKey?: string };
export type RuntimeInteractionIndex = Map<string, InteractionNode>;

/** Keep the layout hierarchy: absolute containers render their children as DOM siblings. */
export function indexRuntimeInteractions(
  nodes: RenderNode[],
  index: RuntimeInteractionIndex = new Map(),
  parentKey?: string,
): RuntimeInteractionIndex {
  for (const node of nodes) {
    const key = node.key ?? node.id;
    index.set(key, { node, parentKey });
    if (node.kind === "container" || node.kind === "layoutList") {
      indexRuntimeInteractions(node.children, index, key);
    }
  }
  return index;
}

export function isRuntimeNodeDisabled(node: RenderNode): boolean {
  return (
    node.interactionDisabled === true ||
    (node.kind === "customDisciplineButton" && node.props.disabled === true)
  );
}

/** Bubble from the clicked element through its logical parents, once per owner. */
export function runtimeClickTargets(
  index: RuntimeInteractionIndex,
  key: string,
): RenderNode[] {
  const targets: RenderNode[] = [];
  let entry = index.get(key);
  while (entry) {
    if (isRuntimeNodeDisabled(entry.node)) return [];
    if (entry.node.clickable) targets.push(entry.node);
    entry = entry.parentKey == null ? undefined : index.get(entry.parentKey);
  }
  return targets;
}

/** Native controls retain their own keyboard and focus behavior. */
export function needsRuntimeButtonSemantics(node: RenderNode): boolean {
  return (
    node.clickable === true &&
    node.kind !== "customDisciplineButton" &&
    node.kind !== "customDisciplineInput" &&
    !(node.kind === "video" && node.controls)
  );
}
